import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/button.tsx";
import { type DataColumn, DataTable } from "../../components/shared/DataTable.tsx";
import { StatusBadge } from "../../components/shared/StatusBadge.tsx";
import { apiRequest } from "../../lib/http.ts";

interface DashboardData {
  automation: { enabled: boolean; dryRun: boolean; approvalRequired: boolean };
  metrics: Record<string, number | null>;
  warnings: string[];
  latestOffers: Array<Record<string, unknown>>;
}

const METRIC_LABELS: Record<string, string> = {
  sourceGroups: "Grupos de origem",
  destinationGroups: "Destinos ativos",
  offersToday: "Ofertas hoje",
  processed: "Processadas",
  manualReview: "Aguardando revisão manual",
  published: "Publicadas",
  pending: "Publicações pendentes",
  failed: "Falhas",
  successRate: "Sucesso",
};

const OFFER_COLUMNS: DataColumn<Record<string, unknown>>[] = [
  { key: "productTitle", label: "Produto" },
  { key: "store", label: "Loja" },
  {
    key: "status",
    label: "Status",
    render: (offer) => <StatusBadge status={String(offer.status ?? "")} />,
  },
  { key: "createdAt", label: "Recebida em" },
];

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiRequest<DashboardData>("/api/v1/dashboard"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar o painel.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleAutomation = async () => {
    if (!data) return;
    setBusy(true);
    setError(null);
    try {
      await apiRequest(
        data.automation.enabled ? "/api/v1/automation/pause" : "/api/v1/automation/resume",
        { method: "POST" },
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível alterar a automação.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2 text-sm">
          <span className="rounded-full border bg-card px-3 py-1 shadow-sm">
            {data?.automation.enabled ? "Automação habilitada" : "Automação pausada"}
          </span>
          <span className="rounded-full border bg-card px-3 py-1 shadow-sm">
            {data?.automation.dryRun ? "Modo simulação" : "Envio real permitido"}
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void load()} disabled={loading}>
            Atualizar
          </Button>
          {data && (
            <Button onClick={() => void toggleAutomation()} disabled={busy}>
              {data.automation.enabled ? "Pausar automação" : "Retomar automação"}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      {data?.warnings.map((warning) => (
        <div
          key={warning}
          className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
        >
          {warning}
        </div>
      ))}

      {loading && !data
        ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-busy="true">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="h-24 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        )
        : (
          <section aria-label="Métricas" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(data?.metrics ?? {}).map(([key, metric]) => (
              <article key={key} className="rounded-lg border bg-card p-4 shadow-sm">
                <p className="text-sm text-muted-foreground">{METRIC_LABELS[key] ?? key}</p>
                <p className="mt-2 text-2xl font-semibold tracking-tight">
                  {metric === null ? "—" : key === "successRate" ? `${metric}%` : metric}
                </p>
              </article>
            ))}
          </section>
        )}

      <section aria-labelledby="latest-offers" className="space-y-3">
        <h3 id="latest-offers" className="text-lg font-semibold">Últimas ofertas capturadas</h3>
        <DataTable
          caption="Últimas ofertas capturadas"
          columns={OFFER_COLUMNS}
          rows={data?.latestOffers ?? []}
          rowKey={(offer, index) => String(offer.id ?? index)}
          emptyTitle="Ainda não há ofertas"
          emptyDescription="Configure a integração WAHA e os grupos de origem para começar a capturar ofertas."
        />
      </section>
    </div>
  );
}
